import type {
  CreateExpenseCategory,
  ExpenseCategory,
  UpdateExpenseCategory,
} from "@home-finance-manager/shared/types/expense_categories"
import { BaseSDK } from "./base-sdk"

export class ExpenseCategoriesSDK extends BaseSDK {
  /**
   * Get all expense categories for an account
   * @returns Array of expense categories
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getCategories(): Promise<ExpenseCategory[]> {
    const response = await this.fetchWithErrorHandling<{ data: ExpenseCategory[] }>(
      `${this.baseUrl}/expense-categories/categories`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<ExpenseCategory[]>(response) || []
  }

  /**
   * Get a specific expense category by ID
   * @param id - The category ID
   * @returns The expense category
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getCategory(id: number): Promise<ExpenseCategory> {
    const response = await this.fetchWithErrorHandling<{ data: ExpenseCategory }>(
      `${this.baseUrl}/expense-categories/categories/${id}`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<ExpenseCategory>(response)
  }

  /**
   * Get expense category with its expenses
   * @param id - The category ID
   * @returns The expense category with expenses
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getCategoryWithExpenses(id: number): Promise<ExpenseCategory & { expenses: any[] }> {
    const response = await this.fetchWithErrorHandling<{
      data: ExpenseCategory & { expenses: any[] }
    }>(`${this.baseUrl}/expense-categories/categories/${id}/expenses`, {
      headers: this.getAuthHeaders(),
    })
    return this.extractData<ExpenseCategory & { expenses: any[] }>(response)
  }

  /**
   * Create a new expense category
   * @param categoryData - The category data
   * @returns The created expense category
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async createCategory(categoryData: CreateExpenseCategory): Promise<ExpenseCategory> {
    const response = await this.fetchWithErrorHandling<{ data: ExpenseCategory }>(
      `${this.baseUrl}/expense-categories/categories`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(categoryData),
      }
    )
    return this.extractData<ExpenseCategory>(response)
  }

  /**
   * Update an expense category
   * @param id - The category ID
   * @param categoryData - The updated category data
   * @returns The updated expense category
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async updateCategory(id: number, categoryData: UpdateExpenseCategory): Promise<ExpenseCategory> {
    const response = await this.fetchWithErrorHandling<{ data: ExpenseCategory }>(
      `${this.baseUrl}/expense-categories/categories/${id}`,
      {
        method: "PUT",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(categoryData),
      }
    )
    return this.extractData<ExpenseCategory>(response)
  }

  /**
   * Delete an expense category
   * @param id - The category ID
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async deleteCategory(id: number): Promise<{ message: string }> {
    return this.fetchWithErrorHandling<{ message: string }>(
      `${this.baseUrl}/expense-categories/categories/${id}`,
      {
        method: "DELETE",
        headers: this.getAuthHeaders(),
      }
    )
  }
}
